import {
    Injector,
    Type,
} from '@angular/core';
import { FeatureFlag } from '@models';
import { Logger } from '@services/logger/abstract.logger';
import { MapperService } from '@services/mapper.service';

import { CurrentUserAPI } from 'main/app-state/current-user/current-user.api';
import { UserStateManager } from 'main/app-state/generics/user-state-manager';
import {
    BehaviorSubject,
    Observable,
} from 'rxjs';
import {
    distinctUntilChanged,
    map,
    tap,
} from 'rxjs/operators';

import { SettingsAPI } from './settings.api';
import { SettingsService } from './settings.service';
import {
    BOOLEAN_SETTINGS,
    BooleanSettings,
    NUMERIC_SETTINGS,
    Setting,
    STRING_SETTINGS,
    StringSettings,
} from './settings.types';

export class SettingsManager extends UserStateManager<any> implements SettingsAPI {
    protected _state = new BehaviorSubject<any>(null);

    private _settingsService: SettingsService;

    constructor(
        injector: Injector,
        user: CurrentUserAPI,
    ) {
        super(user);

        this._settingsService = injector.get<SettingsService>(SettingsService);

        // The Injector doesn't work directly with abstract classes, so manual casting to Type<Logger> has to be done.
        // More details: https://github.com/angular/angular/issues/23611
        this._logStateChanges('Settings', injector.get<Logger>(Logger as Type<Logger>));
    }

    public get passwordComplexity() {
        return this._getMappedPropertyFromState(
            'PasswordComplexity',
            MapperService.passwordComplexityMapper,
            this.value,
        );
    }

    public get$<T extends Setting>(property: T, state?: any):
        T extends StringSettings ? Observable<string | undefined> :
        T extends BooleanSettings ? Observable<boolean | undefined> : Observable<number | undefined>;
    public get$(property: Setting) {
        return this._state.pipe(
            map(state => this.get(property, state)),
            distinctUntilChanged(),
        );
    }

    public get<T extends Setting>(property: T, state?: any):
        T extends StringSettings ? string | undefined :
        T extends BooleanSettings ? boolean | undefined : number | undefined;
    public get(property: Setting, state = this.value): string | boolean | number | undefined {
        if (STRING_SETTINGS.includes(property)) {
            return this._getPropertyFromState(property, state);
        }
        if (BOOLEAN_SETTINGS.includes(property)) {
            return this._getBooleanPropertyFromState(property, state);
        }
        if (NUMERIC_SETTINGS.includes(property)) {
            return this._getNumericPropertyFromState(property, state);
        }
        return undefined;
    }

    public isFeatureEnabled(feature: FeatureFlag) {
        return this._getBooleanPropertyFromState(feature, this.value) || false;
    }

    public fetch() {
        return this._settingsService.get()
            .pipe(
                tap(data => this._state.next(data)),
            );
    }

    public clean() {
        return new Observable<null>((subscriber) => {
            this._state.next(null);
            subscriber.next(null);
            subscriber.complete();
        });
    }

    private _getPropertyFromState(property: string, state: any) {
        return state && state.hasOwnProperty(property) ?
            state[property].toString() as string :
            undefined;
    }

    private _getNumericPropertyFromState(property: string, state: any) {
        return state && state.hasOwnProperty(property) ?
            Number(state[property]) :
            undefined;
    }

    private _getBooleanPropertyFromState(property: string, state: any) {
        return state && state.hasOwnProperty(property) ?
            state[property].toLowerCase() === 'true' :
            undefined;
    }

    private _getMappedPropertyFromState(property: string, mapper: Function, state: any) {
        return state && state.hasOwnProperty(property) ?
            mapper(state[property]) :
            undefined;
    }
}
